/*
 * Copyright (c) Nexign, JSC, 2019-2023.
 */
/**
 * App types
 *
 * @author Mikhail Kokoulin
 * @author Maxim Fedorov
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Andrey Dubov
 * @version 1.23.0
 * @since 1.0.0
 */

import {
    INIT_APP_FAIL,
    INIT_APP_START,
    INIT_APP_SUCCESS,
    LOAD_APP_CONFIG_FAILURE,
    LOAD_APP_CONFIG_SUCCESS,
    SET_APP_ERROR,
    SET_APP_ERROR_CODE,
    SET_INIT_APP_ERROR,
} from './appConstants';
import { IAction, IActionWithPayload } from './createAction';

interface IAppConfigLogin {
    prefix: string;
    showSlider: boolean;
}

export enum EModeSSO {
    b2b = 'B2B',
    sso = 'EXTERNAL',
    soho = 'SOHO',
}

export interface IAuthSSO {
    changePassword: string;
    logout: string;
    mode: EModeSSO;
    login: string;
    redirect: string;
}

export interface IAppFeature {
    enabled: boolean;
    name: EAppFeatures;
    version: number;
}

export enum EAppFeatures {
    internationalization = 'internationalization',
    showUIVersionModal = 'showUIVersionModal',
}

export interface IAppConfig {
    appInfo: string;
    features: IAppFeature[];
    gtmContainerId: string;
    gtmEnabled: boolean;
    login: IAppConfigLogin;
    sso: IAuthSSO;
}

export interface IAppReducerState {
    code: string;
    config: IAppConfig;
    errorMessage: string;
    hasError: boolean;
    hasInitError: boolean;
    isLoaded: boolean;
    isLoading: boolean;
}

export type TInitAppStart = IAction<typeof INIT_APP_START>;

export type TInitAppSuccess = IAction<typeof INIT_APP_SUCCESS>;

export type TInitAppFail = IActionWithPayload<typeof INIT_APP_FAIL, string>;

export type TLoadAppConfigSuccess = IActionWithPayload<typeof LOAD_APP_CONFIG_SUCCESS, IAppConfig>;

export type TLoadAppConfigFailure = IActionWithPayload<typeof LOAD_APP_CONFIG_FAILURE, string>;

/*
 * -------------------------------------------
 *           APP ERROR
 * -------------------------------------------
 */

export interface ISetAppErrorPayload {
    hasError: boolean;
}

export type TSetAppErrorAction = IActionWithPayload<typeof SET_APP_ERROR, ISetAppErrorPayload>;

export interface ISetInitAppErrorPayload {
    code?: string;
    hasInitError: boolean;
}

export type TSetInitAppErrorAction = IActionWithPayload<typeof SET_INIT_APP_ERROR, ISetInitAppErrorPayload>;

export interface ISetAppErrorCodePayload {
    code: string;
}

export type TSetAppErrorCodeAction = IActionWithPayload<typeof SET_APP_ERROR_CODE, ISetAppErrorCodePayload>;
